import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import type { ContactInput, CustomFieldDefinition } from "@hub/shared";
import { Button, Checkbox, Field, IconButton, Input, Select, Switch, Textarea, toast } from "@/ui";
import { invokeEngine, useEngineMutation, useEngineQuery } from "@/engine/hooks";
import { Page } from "@/app/layout/Page";
import { ContactChip } from "@/features/people/ContactChip";

interface Draft {
  firstName: string;
  lastName: string;
  headline: string;
  company: string;
  title: string;
  location: string;
  bio: string;
  birthday: string; // YYYY-MM-DD or --MM-DD
  emails: { value: string; label: string }[];
  phones: { value: string; label: string }[];
  websites: string[];
  linkedin: string;
  twitter: string;
  interests: string;
  isPrivate: boolean;
}

type CustomValues = Record<string, string | string[]>;

/** Normalises a custom field value for comparison and saving: trimmed strings, deduplicated arrays, checkbox "true" or "". */
export function normaliseCustomValue(def: CustomFieldDefinition, v: string | string[] | undefined): string | string[] {
  if (def.fieldType === "multiselect") return Array.from(new Set((Array.isArray(v) ? v : v ? String(v).split(",") : []).map((x) => x.trim()).filter(Boolean)));
  if (def.fieldType === "checkbox") return v === "true" || v === "1" || (Array.isArray(v) && v.includes("true")) ? "true" : "";
  return Array.isArray(v) ? v.join(", ").trim() : (v ?? "").trim();
}

const empty: Draft = { firstName: "", lastName: "", headline: "", company: "", title: "", location: "", bio: "", birthday: "", emails: [{ value: "", label: "" }], phones: [{ value: "", label: "" }], websites: [""], linkedin: "", twitter: "", interests: "", isPrivate: false };

export function ContactEditPage({ mode }: { mode: "create" | "edit" }) {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const detail = useEngineQuery("getContact", { contactId: id }, { enabled: mode === "edit" && !!id });
  const [draft, setDraft] = useState<Draft>(empty);
  const [custom, setCustom] = useState<CustomValues>({});
  const [customInitial, setCustomInitial] = useState<CustomValues>({});
  const [conflicts, setConflicts] = useState<{ value: string; contactIds: string[] }[]>([]);
  const customDefs = useEngineQuery("getCustomFieldDefinitions", undefined);
  const fieldValues = useEngineQuery("getContactFieldValues", { contactId: id }, { enabled: mode === "edit" && !!id });
  const create = useEngineMutation("createContact");
  const update = useEngineMutation("updateContact");
  const findByLookup = useEngineMutation("findContactsByLookup", { invalidate: false, silent: true });

  useEffect(() => {
    if (mode !== "edit" || !detail.data) return;
    const p = detail.data.profile;
    const work = p.workHistory.find((w) => w.isCurrent) ?? p.workHistory[0];
    const emails = detail.data.lookups.filter((l) => l.lookup.type === "email").map((l) => ({ value: l.lookup.value, label: l.label ?? "" }));
    const phones = detail.data.lookups.filter((l) => l.lookup.type === "phone").map((l) => ({ value: l.lookup.value, label: l.label ?? "" }));
    setDraft({
      firstName: p.firstName ?? "",
      lastName: p.lastName ?? "",
      headline: p.headline ?? "",
      company: p.company ?? work?.company ?? "",
      title: p.title ?? work?.title ?? "",
      location: p.location ?? p.addresses[0]?.city ?? "",
      bio: p.bio ?? "",
      birthday: p.birthday ? `${p.birthday.year ?? ""}-${String(p.birthday.month).padStart(2, "0")}-${String(p.birthday.day).padStart(2, "0")}` : "",
      emails: emails.length ? emails : [{ value: "", label: "" }],
      phones: phones.length ? phones : [{ value: "", label: "" }],
      websites: p.websites.length ? p.websites : [""],
      linkedin: p.socialLinks.find((s) => s.platform === "linkedin")?.url ?? "",
      twitter: p.socialLinks.find((s) => s.platform === "twitter")?.url ?? "",
      interests: p.interests.join(", "),
      isPrivate: detail.data.contact.isPrivate,
    });
    setCustom({ ...p.fields });
    setCustomInitial({ ...p.fields });
  }, [mode, detail.data]);

  /** Writes changed custom fields: clears the old rows for a field, then writes the new value(s) with ordinals. */
  const saveCustomFields = async (contactId: string) => {
    for (const def of customDefs.data ?? []) {
      const next = normaliseCustomValue(def, custom[def.fieldName]);
      const prev = normaliseCustomValue(def, customInitial[def.fieldName]);
      if (JSON.stringify(next) === JSON.stringify(prev)) continue;
      const existing = (fieldValues.data ?? []).filter((v) => v.fieldName === def.fieldName && !v.deleted);
      for (const row of existing) await invokeEngine("clearContactFieldValue", { fieldValueId: row.id });
      if (Array.isArray(next)) {
        for (let i = 0; i < next.length; i++) await invokeEngine("setContactFieldValue", { contactId, fieldName: def.fieldName, value: next[i], ordinal: i });
      } else if (next) {
        await invokeEngine("setContactFieldValue", { contactId, fieldName: def.fieldName, value: next });
      }
    }
  };

  // Live conflict check: does any typed email or phone already belong to someone else?
  useEffect(() => {
    const lookups = [...draft.emails.filter((e) => e.value.includes("@")).map((e) => ({ type: "email" as const, value: e.value.trim().toLowerCase() })), ...draft.phones.filter((p) => p.value.replace(/\D/g, "").length >= 7).map((p) => ({ type: "phone" as const, value: p.value.trim() }))];
    if (lookups.length === 0) { setConflicts([]); return; }
    const t = setTimeout(() => {
      findByLookup.mutateAsync(lookups).then((res) => setConflicts(res.filter((r) => r.contactIds.some((cid) => cid !== id)).map((r) => ({ value: r.lookup.value, contactIds: r.contactIds.filter((cid) => cid !== id) })))).catch(() => undefined);
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.emails, draft.phones, id]);

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((d) => ({ ...d, [k]: v }));

  const toInput = (): ContactInput => {
    const [y, m, d] = draft.birthday.split("-");
    return {
      firstName: draft.firstName.trim() || null,
      lastName: draft.lastName.trim() || null,
      headline: draft.headline.trim() || null,
      company: draft.company.trim() || null,
      title: draft.title.trim() || null,
      location: draft.location.trim() || null,
      bio: draft.bio.trim() || null,
      birthday: m && d ? { year: y ? Number(y) : null, month: Number(m), day: Number(d) } : null,
      emails: draft.emails.filter((e) => e.value.trim()).map((e) => ({ value: e.value.trim(), label: e.label.trim() || null })),
      phones: draft.phones.filter((p) => p.value.trim()).map((p) => ({ value: p.value.trim(), label: p.label.trim() || null })),
      websites: draft.websites.map((w) => w.trim()).filter(Boolean),
      socialLinks: [draft.linkedin.trim() ? { platform: "linkedin", url: draft.linkedin.trim() } : null, draft.twitter.trim() ? { platform: "twitter", url: draft.twitter.trim() } : null].filter((x): x is { platform: string; url: string } => !!x),
      interests: draft.interests.split(",").map((s) => s.trim()).filter(Boolean),
      isPrivate: draft.isPrivate,
      source: "manual",
    };
  };

  const submit = async () => {
    if (!draft.firstName.trim() && !draft.lastName.trim() && !draft.emails.some((e) => e.value.trim())) {
      toast("Add at least a name or an email", "danger");
      return;
    }
    try {
      if (mode === "create") {
        const res = await create.mutateAsync(toInput());
        await saveCustomFields(res.contactId);
        toast(res.duplicates.length ? "Person created; possible duplicates found" : "Person created", "success");
        navigate(`/contact/${res.contactId}`, { replace: true });
      } else {
        await update.mutateAsync({ contactId: id, input: toInput() });
        await saveCustomFields(id);
        toast("Saved", "success");
        navigate(`/contact/${id}`, { replace: true });
      }
    } catch (e) {
      toast(e instanceof Error ? e.message : "Could not save the custom fields", "danger");
    }
  };

  const list = (key: "emails" | "phones", placeholder: string) => (
    <div className="u-col" style={{ gap: 6 }}>
      {draft[key].map((item, i) => (
        <div key={i} className="u-row">
          <Input value={item.value} placeholder={placeholder} onChange={(e) => set(key, draft[key].map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))} />
          <Input value={item.label} placeholder="label" style={{ width: 110 }} onChange={(e) => set(key, draft[key].map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
          <IconButton label="Remove" onClick={() => set(key, draft[key].filter((_, j) => j !== i))}><Trash2 size={14} /></IconButton>
        </div>
      ))}
      <Button size="sm" variant="ghost" icon={<Plus size={13} />} onClick={() => set(key, [...draft[key], { value: "", label: "" }])}>Add {key === "emails" ? "email" : "phone"}</Button>
    </div>
  );

  return (
    <Page before={<IconButton label="Back" onClick={() => navigate(-1)}><ArrowLeft size={16} /></IconButton>} title={mode === "create" ? "New person" : `Edit ${detail.data?.profile.displayName ?? ""}`} actions={<><Button variant="ghost" onClick={() => navigate(-1)}>Cancel</Button><Button variant="primary" onClick={submit} loading={create.isPending || update.isPending}>Save</Button></>}>
      <form onSubmit={(e) => { e.preventDefault(); submit(); }} onKeyDown={(e) => (e.metaKey || e.ctrlKey) && e.key === "Enter" && submit()} className="u-col" style={{ gap: 16 }}>
        <div className="u-row" style={{ gap: 12, alignItems: "flex-start" }}>
          <div style={{ flex: 1 }}><Field label="First name"><Input autoFocus value={draft.firstName} onChange={(e) => set("firstName", e.target.value)} /></Field></div>
          <div style={{ flex: 1 }}><Field label="Last name"><Input value={draft.lastName} onChange={(e) => set("lastName", e.target.value)} /></Field></div>
        </div>
        <div className="u-row" style={{ gap: 12, alignItems: "flex-start" }}>
          <div style={{ flex: 1 }}><Field label="Title"><Input value={draft.title} onChange={(e) => set("title", e.target.value)} /></Field></div>
          <div style={{ flex: 1 }}><Field label="Company"><Input value={draft.company} onChange={(e) => set("company", e.target.value)} /></Field></div>
        </div>
        <Field label="Headline" hint="Shown under the name. Defaults to title at company."><Input value={draft.headline} onChange={(e) => set("headline", e.target.value)} /></Field>
        <div className="u-row" style={{ gap: 12, alignItems: "flex-start" }}>
          <div style={{ flex: 1 }}><Field label="Location"><Input value={draft.location} onChange={(e) => set("location", e.target.value)} placeholder="City" /></Field></div>
          <div style={{ width: 200 }}><Field label="Birthday" hint="Year is optional"><Input type="date" value={draft.birthday} onChange={(e) => set("birthday", e.target.value)} /></Field></div>
        </div>
        <Field label="Emails">{list("emails", "name@company.com")}</Field>
        <Field label="Phones">{list("phones", "+1 415 555 0100")}</Field>
        {conflicts.length > 0 && (
          <div style={{ padding: 10, border: "1px solid var(--warning)", background: "var(--warning-soft)", borderRadius: 8 }} data-testid="lookup-conflicts">
            <div style={{ fontWeight: 600 }}>Already on file</div>
            {conflicts.map((c) => (
              <div key={c.value} className="u-row" style={{ gap: 6, marginTop: 4, flexWrap: "wrap" }}>
                <span className="u-small">{c.value} belongs to</span>
                {c.contactIds.map((cid) => <ContactChip key={cid} contactId={cid} />)}
              </div>
            ))}
            <div className="u-small" style={{ marginTop: 6 }}>Saving keeps both records; you can merge them afterwards.</div>
          </div>
        )}
        <div className="u-row" style={{ gap: 12, alignItems: "flex-start" }}>
          <div style={{ flex: 1 }}><Field label="LinkedIn"><Input value={draft.linkedin} onChange={(e) => set("linkedin", e.target.value)} placeholder="https://www.linkedin.com/in/..." /></Field></div>
          <div style={{ flex: 1 }}><Field label="X"><Input value={draft.twitter} onChange={(e) => set("twitter", e.target.value)} placeholder="https://x.com/..." /></Field></div>
        </div>
        <Field label="Websites">
          <div className="u-col" style={{ gap: 6 }}>
            {draft.websites.map((w, i) => (
              <div key={i} className="u-row">
                <Input value={w} placeholder="https://" onChange={(e) => set("websites", draft.websites.map((x, j) => (j === i ? e.target.value : x)))} />
                <IconButton label="Remove" onClick={() => set("websites", draft.websites.filter((_, j) => j !== i))}><Trash2 size={14} /></IconButton>
              </div>
            ))}
            <Button size="sm" variant="ghost" icon={<Plus size={13} />} onClick={() => set("websites", [...draft.websites, ""])}>Add website</Button>
          </div>
        </Field>
        <Field label="Interests" hint="Comma separated"><Input value={draft.interests} onChange={(e) => set("interests", e.target.value)} /></Field>
        <Field label="Bio"><Textarea value={draft.bio} onChange={(e) => set("bio", e.target.value)} rows={3} /></Field>
        {(customDefs.data ?? []).length > 0 && (
          <div className="u-col" style={{ gap: 12, padding: 12, border: "1px solid var(--border)", borderRadius: 8 }} data-testid="custom-field-inputs">
            <div className="u-row" style={{ justifyContent: "space-between" }}>
              <span style={{ fontWeight: 600 }}>Custom fields</span>
              <Link to="/settings/fields" className="u-small">Manage</Link>
            </div>
            {customDefs.data!.map((def) => <CustomFieldInput key={def.fieldName} def={def} value={custom[def.fieldName]} onChange={(v) => setCustom((c) => ({ ...c, [def.fieldName]: v }))} />)}
          </div>
        )}
        <div className="u-row" style={{ justifyContent: "space-between" }}>
          <div>
            <div style={{ fontWeight: 500 }}>Private</div>
            <div className="u-small u-muted">Excluded from matching and never shared.</div>
          </div>
          <Switch on={draft.isPrivate} onChange={(v) => set("isPrivate", v)} label="Private" />
        </div>
      </form>
    </Page>
  );
}

function CustomFieldInput({ def, value, onChange }: { def: CustomFieldDefinition; value: string | string[] | undefined; onChange: (v: string | string[]) => void }) {
  const str = Array.isArray(value) ? value.join(", ") : (value ?? "");
  switch (def.fieldType) {
    case "checkbox":
      return (
        <div className="u-row" style={{ justifyContent: "space-between" }}>
          <span style={{ fontWeight: 500 }}>{def.displayName}</span>
          <Switch on={str === "true" || str === "1"} onChange={(v) => onChange(v ? "true" : "")} label={def.displayName} />
        </div>
      );
    case "select":
      return (
        <Field label={def.displayName}>
          <Select value={str} onChange={(e) => onChange(e.target.value)}>
            <option value="">Not set</option>
            {def.options.map((o) => <option key={o} value={o}>{o}</option>)}
          </Select>
        </Field>
      );
    case "multiselect": {
      const selected = new Set(Array.isArray(value) ? value : str ? str.split(",").map((x) => x.trim()).filter(Boolean) : []);
      return (
        <Field label={def.displayName}>
          <div className="u-row" style={{ gap: 10, flexWrap: "wrap" }}>
            {def.options.map((o) => (
              <label key={o} className="u-row u-small" style={{ gap: 4 }}>
                <Checkbox checked={selected.has(o)} onChange={(e) => { const next = new Set(selected); if (e.target.checked) next.add(o); else next.delete(o); onChange(Array.from(next)); }} /> {o}
              </label>
            ))}
            {def.options.length === 0 && <span className="u-small u-muted">No options defined yet.</span>}
          </div>
        </Field>
      );
    }
    case "number":
      return <Field label={def.displayName}><Input type="number" value={str} onChange={(e) => onChange(e.target.value)} /></Field>;
    case "date":
      return <Field label={def.displayName}><Input type="date" value={str} onChange={(e) => onChange(e.target.value)} /></Field>;
    case "url":
      return <Field label={def.displayName}><Input type="url" value={str} placeholder="https://" onChange={(e) => onChange(e.target.value)} /></Field>;
    default:
      return <Field label={def.displayName}><Input value={str} onChange={(e) => onChange(e.target.value)} /></Field>;
  }
}
